import {
  IconBrandTelegram,
  IconCircleCheck,
  IconCircleX,
  IconClockHour3,
  IconShoppingBag,
  IconShoppingBagCheck,
  IconShoppingBagEdit,
  IconShoppingBagExclamation,
  IconShoppingBagX,
  IconWritingSign,
} from '@tabler/icons-react';
import { EnrollmentStatus } from '@zorro/clients';
import { PillColor } from '@zorro/design-system/v2/Pill';
import {
  CoverageEndedIconNoBackground,
  IconNotApplicable,
} from '@zorro/shared/assets';
import { Box, Icon } from '@zorro/web/design-system';
import { ReactNode } from 'react';

interface StatusIconProps {
  size?: string | number;
  withBackground?: boolean;
}

type IconWrapperProps = {
  size: string | number;
  children: ReactNode;
};

const IconWrapper = ({ size, children }: IconWrapperProps) => {
  return (
    <Box
      style={{
        width: size,
        height: size,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {children}
    </Box>
  );
};

const calculateIconSize = (
  size: string | number,
  withBackground: boolean
): string | number => {
  if (!withBackground && typeof size === 'string') {
    const sizeStr = String(size);
    const numericValue = parseFloat(sizeStr);
    const unit = sizeStr.substring(String(numericValue).length);
    return `${(numericValue * 0.7).toFixed(2)}${unit}`;
  }
  if (!withBackground && typeof size === 'number') {
    return Math.floor(size * 0.7);
  }
  return size;
};

const ICON_COLOR_NO_BACKGROUND = 'var(--color-icon-stroke-tag-gray)';

export function PendingElectionWindowIcon({
  size = 36,
  withBackground = true,
}: StatusIconProps) {
  const iconSize = calculateIconSize(size, withBackground);

  return (
    <IconWrapper size={size}>
      <Icon
        icon={IconShoppingBag}
        color={
          withBackground
            ? 'var(--color-icon-stroke-tag-gray)'
            : ICON_COLOR_NO_BACKGROUND
        }
        strokeWidth={1.5}
        style={{
          borderRadius: '50%',
          backgroundColor: withBackground
            ? 'var(--color-background-tag-default)'
            : 'transparent',
          padding: withBackground ? '0.25rem' : 0,
          boxSizing: 'border-box',
        }}
        size={iconSize}
      />
    </IconWrapper>
  );
}

export function ElectionActiveHasNotStartedIcon({
  size = 36,
  withBackground = true,
}: StatusIconProps) {
  const iconSize = calculateIconSize(size, withBackground);

  return (
    <IconWrapper size={size}>
      <Icon
        icon={IconShoppingBagExclamation}
        color={
          withBackground
            ? 'var(--color-icon-stroke-tag-yellow)'
            : ICON_COLOR_NO_BACKGROUND
        }
        style={{
          borderRadius: '50%',
          backgroundColor: withBackground
            ? 'var(--color-background-tag-yellow)'
            : 'transparent',
          padding: withBackground ? '0.25rem' : 0,
        }}
        size={iconSize}
      />
    </IconWrapper>
  );
}

export function ElectionActiveStartedIcon({
  size = 36,
  withBackground = true,
}: StatusIconProps) {
  const iconSize = calculateIconSize(size, withBackground);

  return (
    <IconWrapper size={size}>
      <Icon
        icon={IconShoppingBagEdit}
        color={
          withBackground
            ? 'var(--color-icon-stroke-tag-orange)'
            : ICON_COLOR_NO_BACKGROUND
        }
        style={{
          borderRadius: '50%',
          backgroundColor: withBackground
            ? 'var(--color-background-tag-orange)'
            : 'transparent',
          padding: withBackground ? '0.25rem' : 0,
        }}
        size={iconSize}
      />
    </IconWrapper>
  );
}

export function ElectionSubmittedIcon({
  size = 36,
  withBackground = true,
}: StatusIconProps) {
  const iconSize = calculateIconSize(size, withBackground);

  return (
    <IconWrapper size={size}>
      <Icon
        icon={IconShoppingBagCheck}
        color={
          withBackground
            ? 'var(--color-icon-stroke-tag-indigo)'
            : ICON_COLOR_NO_BACKGROUND
        }
        style={{
          borderRadius: '50%',
          backgroundColor: withBackground
            ? 'var(--color-background-tag-indigo)'
            : 'transparent',
          padding: withBackground ? '0.25rem' : 0,
        }}
        size={iconSize}
      />
    </IconWrapper>
  );
}

export function WaivedElectionIcon({
  size = 36,
  withBackground = true,
}: StatusIconProps) {
  const iconSize = calculateIconSize(size, withBackground);

  return (
    <IconWrapper size={size}>
      <Icon
        icon={IconShoppingBagX}
        color={
          withBackground
            ? 'var(--color-icon-stroke-tag-steel)'
            : ICON_COLOR_NO_BACKGROUND
        }
        style={{
          borderRadius: '50%',
          backgroundColor: withBackground
            ? 'var(--color-background-tag-steel)'
            : 'transparent',
          padding: withBackground ? '0.25rem' : 0,
        }}
        size={iconSize}
      />
    </IconWrapper>
  );
}

export function DeadlinePassedIcon({
  size = 36,
  withBackground = true,
}: StatusIconProps) {
  const iconSize = calculateIconSize(size, withBackground);

  return (
    <IconWrapper size={size}>
      <Icon
        icon={IconClockHour3}
        color={
          withBackground
            ? 'var(--color-icon-stroke-tag-red)'
            : ICON_COLOR_NO_BACKGROUND
        }
        style={{
          borderRadius: '50%',
          backgroundColor: withBackground
            ? 'var(--color-background-tag-red)'
            : 'transparent',
          padding: withBackground ? '0.25rem' : 0,
        }}
        size={iconSize}
      />
    </IconWrapper>
  );
}

export function EnrollmentConfirmedIcon({
  size = 36,
  withBackground = true,
}: StatusIconProps) {
  const iconSize = calculateIconSize(size, withBackground);

  return (
    <IconWrapper size={size}>
      <Icon
        icon={IconWritingSign}
        color={
          withBackground
            ? 'var(--color-icon-stroke-tag-green)'
            : ICON_COLOR_NO_BACKGROUND
        }
        style={{
          borderRadius: '50%',
          backgroundColor: withBackground
            ? 'var(--color-background-tag-green)'
            : 'transparent',
          padding: withBackground ? '0.25rem' : 0,
        }}
        size={iconSize}
      />
    </IconWrapper>
  );
}

export function CarrierApplicationSentIcon({
  size = 36,
  withBackground = true,
}: StatusIconProps) {
  const iconSize = calculateIconSize(size, withBackground);

  return (
    <IconWrapper size={size}>
      <Icon
        icon={IconBrandTelegram}
        color={
          withBackground
            ? 'var(--color-icon-stroke-tag-blue)'
            : ICON_COLOR_NO_BACKGROUND
        }
        style={{
          borderRadius: '50%',
          backgroundColor: withBackground
            ? 'var(--color-background-tag-blue)'
            : 'transparent',
          padding: withBackground ? '0.25rem' : 0,
        }}
        size={iconSize}
      />
    </IconWrapper>
  );
}

export function CoverageEndedIcon({
  size = 36,
  withBackground = true,
}: StatusIconProps) {
  const iconSize = calculateIconSize(size, withBackground);

  return (
    <IconWrapper size={size}>
      <Icon
        icon={CoverageEndedIconNoBackground}
        color={
          withBackground
            ? 'var(--color-icon-stroke-tag-steel)'
            : ICON_COLOR_NO_BACKGROUND
        }
        style={{
          borderRadius: '50%',
          backgroundColor: withBackground
            ? 'var(--color-background-tag-steel)'
            : 'transparent',
          padding: withBackground ? '0.25rem' : 0,
        }}
        size={iconSize}
      />
    </IconWrapper>
  );
}

export function ActiveCoverageIcon({
  size = 36,
  withBackground = true,
}: StatusIconProps) {
  const iconSize = calculateIconSize(size, withBackground);

  return (
    <IconWrapper size={size}>
      <Icon
        icon={IconCircleCheck}
        color={
          withBackground
            ? 'var(--color-icon-stroke-tag-green)'
            : ICON_COLOR_NO_BACKGROUND
        }
        style={{
          borderRadius: '50%',
          backgroundColor: withBackground
            ? 'var(--color-background-tag-green)'
            : 'transparent',
          padding: withBackground ? '0.25rem' : 0,
        }}
        size={iconSize}
      />
    </IconWrapper>
  );
}

export function WaivedCoverageIcon({
  size = 36,
  withBackground = true,
}: StatusIconProps) {
  const iconSize = calculateIconSize(size, withBackground);

  return (
    <IconWrapper size={size}>
      <Icon
        icon={IconCircleX}
        color={
          withBackground
            ? 'var(--color-icon-stroke-tag-steel)'
            : ICON_COLOR_NO_BACKGROUND
        }
        style={{
          borderRadius: '50%',
          backgroundColor: withBackground
            ? 'var(--color-background-tag-steel)'
            : 'transparent',
          padding: withBackground ? '0.25rem' : 0,
        }}
        size={iconSize}
      />
    </IconWrapper>
  );
}

export function NoEnrollmentsIcon({
  size = 36,
  withBackground = true,
}: StatusIconProps) {
  const iconSize = calculateIconSize(size, withBackground);

  return (
    <IconWrapper size={size}>
      <Box
        style={{
          borderRadius: '50%',
          backgroundColor: withBackground
            ? 'var(--color-background-tag-pink)'
            : 'transparent',
          padding: withBackground ? '0.25rem' : 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: withBackground
            ? 'var(--color-icon-stroke-tag-pink)'
            : ICON_COLOR_NO_BACKGROUND,
        }}
      >
        <IconNotApplicable style={{ width: iconSize, height: iconSize }} />
      </Box>
    </IconWrapper>
  );
}

/**
 * Color + bare icon per enrollment status, for the v2 `Pill` (`with-icon`
 * variant). The Pill draws the 24px colored circle and sizes/colors the icon
 * via CSS, so icons are rendered bare here (no self-drawn background).
 */
export const enrollmentStatusBadgeConfig: Record<
  EnrollmentStatus,
  { color: PillColor; icon: ReactNode }
> = {
  [EnrollmentStatus.PENDING_ELECTION_WINDOW]: {
    color: 'gray',
    icon: <IconShoppingBag />,
  },
  [EnrollmentStatus.ELECTION_ACTIVE]: {
    color: 'orange',
    icon: <IconShoppingBagEdit />,
  },
  [EnrollmentStatus.ELECTION_ACTIVE_HAS_NOT_STARTED]: {
    color: 'yellow',
    icon: <IconShoppingBagExclamation />,
  },
  [EnrollmentStatus.ELECTION_ACTIVE_STARTED]: {
    color: 'orange',
    icon: <IconShoppingBagEdit />,
  },
  [EnrollmentStatus.ELECTION_SUBMITTED]: {
    color: 'indigo',
    icon: <IconShoppingBagCheck />,
  },
  [EnrollmentStatus.WAIVED_ELECTION]: {
    color: 'steel',
    icon: <IconShoppingBagX />,
  },
  [EnrollmentStatus.DEADLINE_PASSED]: {
    color: 'red',
    icon: <IconClockHour3 />,
  },
  [EnrollmentStatus.ENROLLMENT_CONFIRMED]: {
    color: 'green',
    icon: <IconWritingSign />,
  },
  [EnrollmentStatus.ENROLLMENT_IN_PROCESS]: {
    color: 'blue',
    icon: <IconBrandTelegram />,
  },
  [EnrollmentStatus.COVERAGE_ENDED]: {
    color: 'steel',
    icon: <CoverageEndedIconNoBackground color="currentColor" />,
  },
  [EnrollmentStatus.ACTIVE_COVERAGE]: {
    color: 'green',
    icon: <IconCircleCheck />,
  },
  [EnrollmentStatus.WAIVED_COVERAGE]: {
    color: 'steel',
    icon: <IconCircleX />,
  },
  [EnrollmentStatus.NO_ENROLLMENTS]: {
    color: 'pink',
    icon: <IconNotApplicable />,
  },
};
